"use client";

/**
 * EduMock — animated mock of the askFinz Edu workspace for the
 * /apps/edu marketing page.
 *
 * Three-pane lesson workspace: course tree on the left (one expanded module
 * with six lessons), a lesson editor in the centre with auto-generated
 * "Knowledge check" callouts that build up question by question, and a
 * "Tutor mode" panel on the right where a student question is answered
 * with cited textbook references.
 *
 * Lives in askfinz.com only — no backend, no fetch, no auth.
 */
import { useEffect, useRef, useState } from "react";
import {
  BookOpen,
  FolderOpen,
  Folder,
  FileText,
  GraduationCap,
  Lightbulb,
  CheckCircle2,
  CircleHelp,
  Sparkles,
} from "lucide-react";

/* ────────── Course tree ────────── */

type Module = {
  id: string;
  title: string;
  open?: boolean;
  lessons?: { id: string; title: string; done?: boolean; active?: boolean }[];
};

const COURSE: Module[] = [
  { id: "m1", title: "1 · Foundations" },
  {
    id: "m2",
    title: "2 · Distributions & inference",
    open: true,
    lessons: [
      { id: "l1", title: "Sample spaces", done: true },
      { id: "l2", title: "Probability densities", done: true },
      { id: "l3", title: "Expectation & variance", done: true },
      { id: "l4", title: "Hypothesis testing", active: true },
      { id: "l5", title: "Confidence intervals" },
      { id: "l6", title: "Power & effect size" },
    ],
  },
  { id: "m3", title: "3 · Regression" },
  { id: "m4", title: "4 · Bayesian methods" },
];

/* ────────── Lesson body + auto-generated quiz ────────── */

const LESSON_BLOCKS = [
  { kind: "h", text: "Hypothesis testing" },
  {
    kind: "p",
    text:
      "A hypothesis test is a procedure for deciding between two claims about a population on the basis of a sample. The null hypothesis is the default position; the alternative is what we would conclude if the data are sufficiently inconsistent with the null.",
  },
  { kind: "h2", text: "The recipe" },
  { kind: "li", text: "State the null and alternative hypotheses." },
  { kind: "li", text: "Choose a significance level α." },
  { kind: "li", text: "Compute a test statistic from the sample." },
  { kind: "li", text: "Compare against the critical value, or convert to a p-value." },
] as const;

const QUIZ = [
  "Which hypothesis represents the status-quo claim?",
  "What does a p-value of 0.03 mean at α = 0.05?",
  "Increasing the sample size affects which of: bias, variance, both?",
  "Type I and Type II errors — describe each in one sentence.",
  "Give an example where a one-tailed test is more appropriate than two-tailed.",
];

/* ────────── Tutor exchange ────────── */

const TUTOR_QUESTION = "Why do we use α = 0.05 by default?";
const TUTOR_ANSWER =
  "It's a convention, not a law. Fisher proposed 0.05 as a working threshold in 1925; the field standardised on it for comparability. In high-stakes domains people prefer 0.01 or 0.001 — and Bayesian framings sidestep the threshold problem entirely.";
const TUTOR_CITES = [
  { tag: "Wasserman §10.2", page: "p. 152" },
  { tag: "Casella & Berger §8.3", page: "p. 374" },
];

/* ────────── Component ────────── */

// First-paint: lesson body is already fully populated (course tree is static
// + every text block visible). The script's job becomes the auto-quiz
// generation + tutor-mode answer, not building the lesson.
const INITIAL_BLOCK_STEP = LESSON_BLOCKS.length;

export function EduMock() {
  const [blockStep, setBlockStep] = useState(INITIAL_BLOCK_STEP);  // how many lesson blocks visible
  const [quizStep, setQuizStep] = useState(0);    // 0..QUIZ.length
  const [tutorPhase, setTutorPhase] = useState<"idle" | "asked" | "typing" | "done">("idle");
  const [tutorChars, setTutorChars] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // Reset to the partial-fill snapshot — lesson body stays, quiz + tutor
      // reset to "about to start".
      setBlockStep(INITIAL_BLOCK_STEP);
      setQuizStep(0);
      setTutorPhase("idle");
      setTutorChars(0);

      // Lesson body is already in place — skip the block-by-block reveal and
      // jump straight to the quiz + tutor.
      const quizStart = 400;
      QUIZ.forEach((_, i) => {
        schedule(() => setQuizStep(i + 1), quizStart + i * 380);
      });

      // Tutor exchange in parallel-ish — student asks early, agent types.
      schedule(() => setTutorPhase("asked"), 600);
      schedule(() => setTutorPhase("typing"), 1600);
      const total = TUTOR_ANSWER.length;
      const frames = 36;
      for (let f = 1; f <= frames; f++) {
        schedule(() => setTutorChars(Math.ceil((total * f) / frames)), 1600 + f * 50);
      }
      schedule(() => {
        setTutorChars(total);
        setTutorPhase("done");
      }, 1600 + frames * 50 + 200);

      // Loop after dwell on completion.
      const end = Math.max(quizStart + QUIZ.length * 380, 1600 + frames * 50 + 200);
      schedule(runCycle, end + 3500);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            learn.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <GraduationCap className="h-3 w-3" />
            Course · Statistics
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[480px]">
          {/* Course tree */}
          <div className="hidden md:flex col-span-3 flex-col border-r hairline bg-foreground/[0.02] p-2 text-[12px]">
            <div className="px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              Course outline
            </div>
            {COURSE.map((m) => (
              <div key={m.id} className="mt-1">
                <div className="flex items-center gap-1.5 py-1 px-2 rounded text-foreground/80">
                  {m.open ? <FolderOpen className="h-3 w-3 text-foreground/65" /> : <Folder className="h-3 w-3 text-foreground/65" />}
                  <span>{m.title}</span>
                </div>
                {m.open && m.lessons && (
                  <div className="ml-2 border-l hairline pl-2">
                    {m.lessons.map((l, li) => (
                      <div
                        key={l.id}
                        className={
                          "flex items-center gap-1.5 py-0.5 px-2 rounded text-[12px] transition " +
                          (l.active
                            ? "bg-foreground text-background"
                            : l.done
                            ? "text-foreground/65 hover:text-foreground/75 hover:bg-foreground/[0.04]"
                            : "text-foreground/75 hover:bg-foreground/[0.04]")
                        }
                      >
                        {l.done ? (
                          <CheckCircle2 className="h-3 w-3 opacity-65" />
                        ) : (
                          <FileText className="h-3 w-3 opacity-65" />
                        )}
                        <span className="truncate">{l.title}</span>
                        <span className={"ml-auto text-[9px] font-mono " + (l.active ? "text-background/65" : "text-foreground/65")}>
                          {l.done ? "✓" : `${10 + li * 4}m`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-auto px-2 pt-2 border-t hairline text-[10px] font-mono text-foreground/65">
              3 of 6 lessons · 50%
            </div>
          </div>

          {/* Lesson editor */}
          <div className="col-span-12 md:col-span-6 flex flex-col">
            <div className="px-4 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <BookOpen className="h-3 w-3" />
              Lesson · Hypothesis testing
              <span className="ml-auto font-mono normal-case tracking-normal text-foreground/65">draft</span>
            </div>
            <div className="flex-1 px-5 py-5 text-[13px] leading-[1.7] text-foreground/85 space-y-3">
              {LESSON_BLOCKS.slice(0, blockStep).map((b, i) => {
                if (b.kind === "h") {
                  return (
                    <h3 key={i} className="font-medium tracking-tight text-foreground/95 animate-[blur-fade-in_0.4s_ease-out]">
                      {b.text}
                    </h3>
                  );
                }
                if (b.kind === "h2") {
                  return (
                    <div key={i} className="text-[12px] tracking-[0.14em] uppercase text-foreground/65 mt-1 animate-[blur-fade-in_0.4s_ease-out]">
                      {b.text}
                    </div>
                  );
                }
                if (b.kind === "li") {
                  return (
                    <div key={i} className="flex items-start gap-2 animate-[blur-fade-in_0.4s_ease-out]">
                      <span className="mt-2 h-1 w-1 rounded-full bg-foreground/45" />
                      <span>{b.text}</span>
                    </div>
                  );
                }
                return (
                  <p key={i} className="animate-[blur-fade-in_0.4s_ease-out]">
                    {b.text}
                  </p>
                );
              })}

              {/* Knowledge check callout, materialises once lesson body is in */}
              {blockStep >= LESSON_BLOCKS.length && (
                <div className="mt-4 rounded-2xl border hairline-strong bg-foreground/[0.03] p-4 animate-[blur-fade-in_0.5s_ease-out]">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                    <Lightbulb className="h-3 w-3" />
                    Knowledge check
                    <span className="ml-auto font-mono normal-case tracking-normal text-foreground/65">
                      {Math.min(quizStep, QUIZ.length)}/{QUIZ.length}
                    </span>
                  </div>
                  <ol className="mt-3 space-y-1.5">
                    {QUIZ.slice(0, quizStep).map((q, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[12.5px] text-foreground/85 animate-[blur-fade-in_0.35s_ease-out]"
                      >
                        <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full border hairline-strong text-[10px] font-mono text-foreground/65">
                          {i + 1}
                        </span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ol>
                  {quizStep < QUIZ.length && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-foreground/65">
                      <Sparkles className="h-3 w-3" />
                      Agent drafting next question…
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tutor mode */}
          <div className="col-span-12 md:col-span-3 flex flex-col border-t md:border-t-0 md:border-l hairline bg-foreground/[0.02]">
            <div className="px-3 py-2.5 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
              Tutor mode · live
            </div>
            <div className="flex-1 p-3 space-y-3 text-[12px]">
              {/* Student question */}
              {tutorPhase !== "idle" && (
                <div className="self-start animate-[blur-fade-in_0.4s_ease-out]">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-foreground/65 mb-1">
                    <CircleHelp className="h-3 w-3" />
                    Student
                  </div>
                  <div className="rounded-xl rounded-tl-md border hairline bg-background px-3 py-2 text-foreground/85 leading-relaxed">
                    {TUTOR_QUESTION}
                  </div>
                </div>
              )}

              {/* Tutor answer */}
              {(tutorPhase === "typing" || tutorPhase === "done") && (
                <div className="self-start animate-[blur-fade-in_0.4s_ease-out]">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-foreground/65 mb-1">
                    <Sparkles className="h-3 w-3" />
                    Tutor
                  </div>
                  <div className="rounded-xl rounded-tl-md border hairline-strong bg-foreground/[0.02] px-3 py-2 text-foreground/85 leading-relaxed">
                    {TUTOR_ANSWER.slice(0, tutorChars)}
                    {tutorPhase === "typing" && tutorChars < TUTOR_ANSWER.length && (
                      <span className="inline-block h-3 w-1 bg-foreground/55 align-middle ml-0.5 animate-pulse" />
                    )}
                  </div>
                  {tutorPhase === "done" && (
                    <div className="mt-2 space-y-1 animate-[blur-fade-in_0.4s_ease-out]">
                      <div className="text-[10px] uppercase tracking-[0.16em] text-foreground/65">Cited</div>
                      {TUTOR_CITES.map((c, i) => (
                        <div key={i} className="rounded-md border hairline bg-background px-2 py-1.5 text-[11px] flex items-center gap-2">
                          <BookOpen className="h-3 w-3 text-foreground/65" />
                          <span className="text-foreground/80 font-mono">{c.tag}</span>
                          <span className="ml-auto text-foreground/65 font-mono">{c.page}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
